import { Model, Factory, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randProductName,
  randNumber,
  randPastDate,
  randUuid,
  randChanceBoolean,
} from '@ngneat/falso'
import { sortable, pageable, createErrorResponse } from './utils.js'
import type { SchemaAttributes } from '../types.js'
import type { MirageServer, MockServer } from './server.js'

export type HomeDeliveryItemData = SchemaAttributes<'HomeDeliveryItem'>

export const model = Model.extend({
  order: belongsTo('homeDeliveryOrder'),
  patient: belongsTo('patient'),
  prescriber: belongsTo('user'),
}) as ModelDefinition<HomeDeliveryItemData>

export const factory = Factory.extend<HomeDeliveryItemData>({
  name() {
    return randProductName()
  },
  order_date() {
    return randPastDate().toISOString()
  },
  amount_str() {
    const amount = randNumber({ min: 1, max: 10 })
    return `${amount} ${amount === 1 ? 'package' : 'packages'}`
  },
  status() {
    return rand(['CANCELLED', 'PROCESSING', 'QUARANTINED'])
  },
  internal_status() {
    const isActive = randChanceBoolean({ chanceTrue: 0.75 })
    if (isActive) {
      return 'ACTIVE'
    }
    return rand(['CREDITED', 'REPLACED'])
  },
  consultation_id() {
    return randUuid()
  },
  invoice_id() {
    return randUuid()
  },
  afterCreate(homeDeliveryItem, server) {
    if (!homeDeliveryItem.order) {
      homeDeliveryItem.update({ order: server.create('homeDeliveryOrder') })
    }
    if (!homeDeliveryItem.patient) {
      homeDeliveryItem.update({ patient: server.create('patient') })
    }
    if (!homeDeliveryItem.prescriber) {
      homeDeliveryItem.update({ prescriber: server.create('user') })
    }
  },
})

export function seeds(server: MockServer) {
  server.createList('homeDelivery', 20)
}

export function routes(server: MockServer) {
  server.get(
    '/home_delivery_items',
    function (this: MirageServer, schema, request) {
      const homeDeliveryItems = schema.all('homeDelivery')

      const sorted = sortable(homeDeliveryItems, request)

      const { data, meta } = pageable(sorted, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )

  server.patch(
    '/home_delivery_items/:id/update_status',
    function (this: MirageServer, schema, request) {
      const id = request.params.id
      const body = JSON.parse(request.requestBody)
      const status = body?.data?.attributes?.status
      const item = schema.find('homeDelivery', id)

      if (!item) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: 'Not found' }],
        })
      }

      if (status) {
        item.update({ status })
      }

      return item
    },
  )
}
